import { AnyAction, createAction } from '@reduxjs/toolkit'

import { curryActionForPath } from 'helpers/curry'
import { serializeColumns } from 'helpers/tables'
import { CobaltPath } from 'routing/Path'
import {
    ActionForPathPayload,
    Field,
    FieldSerialized,
    Pagination,
    PeriodDeltaType,
} from 'types'

import {
    downloadDataFailure,
    downloadDataRequest,
    downloadDataSuccess,
    fetchDataFailure,
    fetchDataRequest,
    fetchDataSuccess,
    setDataLoading,
    updateSorter,
} from './data'

interface TableColumnsConfigPayload {
    path: CobaltPath | string
    columns: FieldSerialized[]
}

// Update Table Pagination
export const updateTablePagination = createAction<
    ActionForPathPayload<Partial<Pagination>>
>('UPDATE_TABLE_PAGINATION')
export const makeUpdateTablePagination = curryActionForPath(
    updateTablePagination
)

// Update Table Sorter
export const makeUpdateTableSorter = curryActionForPath(updateSorter)

// Update Period Delta Columns
export const updateTablePeriodDeltaVisibility = createAction<
    ActionForPathPayload<boolean>
>('UPDATE_TABLE_PERIOD_DELTA_VISIBILITY')
export const makeUpdateTablePeriodDeltaVisibility = curryActionForPath(
    updateTablePeriodDeltaVisibility
)
export const updateTablePeriodDeltaType = createAction<
    ActionForPathPayload<PeriodDeltaType>
>('UPDATE_TABLE_PERIOD_DELTA_TYPE')
export const makeUpdateTablePeriodDeltaType = curryActionForPath(
    updateTablePeriodDeltaType
)
export const updateTablePeriodDeltaDateRange = createAction<
    ActionForPathPayload<string[]>
>('UPDATE_TABLE_PERIOD_DELTA_DATE_RANGE')
export const makeUpdateTablePeriodDeltaDateRange = curryActionForPath(
    updateTablePeriodDeltaDateRange
)

// Total Row
export const updateTableTotalRowVisibility = createAction<
    ActionForPathPayload<boolean>
>('UPDATE_TABLE_TOTAL_ROW_VISIBILITY')
export const makeUpdateTableTotalRowVisibility = curryActionForPath(
    updateTableTotalRowVisibility
)

// Update Table Column Configuration
const curryActionForColumns =
    (action: (payload: TableColumnsConfigPayload) => AnyAction) =>
    (path: CobaltPath | string) =>
    (columns: Field<unknown>[]): AnyAction =>
        action({
            path,
            columns: serializeColumns(columns),
        })
export const updateTableColumnsConfig = createAction<TableColumnsConfigPayload>(
    'UPDATE_TABLE_COLUMNS_CONFIG'
)
export const makeUpdateTableColumnsConfig = curryActionForColumns(
    updateTableColumnsConfig
)

// Table Loading State (used to explicitly set table loading state)
export const makeSetTableLoading = curryActionForPath(setDataLoading)

// Table Fetching
export const makeFetchTableRequest = curryActionForPath(fetchDataRequest)
export const makeFetchTableSuccess = curryActionForPath(fetchDataSuccess)
export const makeFetchTableFailure = curryActionForPath(fetchDataFailure)

// Update Table Records
export const updateTableRecord = createAction<
    ActionForPathPayload<{ rowIndex: number; record: unknown }>
>('UPDATE_TABLE_RECORD')
export const makeUpdateTableRecord = curryActionForPath(updateTableRecord)

// Download Table
export const makeDownloadTableRequest = curryActionForPath(downloadDataRequest)
export const makeDownloadTableSuccess = curryActionForPath(downloadDataSuccess)
export const makeDownloadTableFailure = curryActionForPath(downloadDataFailure)
